import React from 'react';
import { AlertTriangle, Clock, ShieldOff } from 'lucide-react';

function formatTokens(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toString();
}

function formatResetDate(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
}

export default function ChatRestrictionBanner({ reason, usageStatus }) {
  if (reason === 'manual_cooldown') {
    return (
      <div className="mx-auto max-w-2xl mb-3 px-4 py-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 flex items-start gap-3">
        <ShieldOff className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            Chat usage limited
          </p>
          <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-0.5">
            Your chat usage has been limited in order to maintain service quality. Please contact support if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  if (reason === 'weekly_limit_exceeded') {
    const usage = usageStatus?.weekly_token_usage || 0;
    const limit = usageStatus?.weekly_token_limit || 2_000_000;
    const resetAt = usageStatus?.reset_at;

    return (
      <div className="mx-auto max-w-2xl mb-3 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
            Weekly usage limit reached
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
            You've used {formatTokens(usage)} of your {formatTokens(limit)} weekly token limit.
            {resetAt && <> Your limit resets on <strong>{formatResetDate(resetAt)}</strong>.</>}
          </p>
          <div className="mt-2 h-1.5 rounded-full bg-amber-200 dark:bg-amber-800 overflow-hidden">
            <div className="h-full rounded-full bg-amber-500" style={{ width: '100%' }} />
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export function ChatUsageIndicator({ usageStatus }) {
  if (!usageStatus) return null;

  const { weekly_token_usage: usage, weekly_token_limit: limit, usage_percentage: pct } = usageStatus;

  // Only show when usage > 50%
  if (pct <= 50) return null;

  const barColor = pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-blue-500';
  const textColor = pct >= 90 ? 'text-red-500' : pct >= 75 ? 'text-amber-500' : 'text-gray-400';

  return (
    <div className="flex items-center gap-2 px-1">
      <Clock className={`w-3 h-3 ${textColor}`} />
      <div className="flex items-center gap-1.5">
        <div className="w-16 h-1 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
          <div className={`h-full rounded-full ${barColor}`} style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
        <span className={`text-[10px] ${textColor}`}>
          {formatTokens(usage)} / {formatTokens(limit)}
        </span>
      </div>
    </div>
  );
}
